import React, { useEffect, useRef } from "react";
import "./WaveBG.scss";
/**
 * Utility function to generate a smooth sine wave path string for SVG <path>.
 *
 * @param {number} width     - Total width of the SVG viewport.
 * @param {number} height    - Total height of the SVG viewport.
 * @param {number} points    - Number of sample points along the wave.
 * @param {number} amplitude - Height of the wave (peak value).
 * @param {number} frequency - Controls how many oscillations fit in the width.
 * @param {number} phase     - Horizontal offset for the sine wave.
 * @param {number} baseline  - Vertical position (0–1) relative to SVG height.
 * @param {number} pad       - Horizontal padding for closing the path.
 *
 * @returns {string} - A path `d` string that can be used in an SVG <path>.
 */
function createSinePath(
  width,
  height,
  points,
  amplitude,
  frequency,
  phase,
  baseline = 0.5,
  pad = 10
) {
  const cy = height * baseline; // vertical center line of the wave
  const step = width / (points - 1);

  let d = "";
  for (let i = 0; i < points; i++) {
    const x = i * step;

    // "taper" reduces wave amplitude near the edges, giving it a more natural look
    const taper = 1 - Math.abs((x - width / 2) / (width / 2));

    const y = cy + Math.sin(x * frequency + phase) * amplitude * taper;

    // Start the path with "M", then continue with "L" commands
    d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
  }

  // Close the path by connecting it to the bottom of the SVG rectangle
  d += ` L ${width + pad} ${height + pad} L ${-pad} ${height + pad} Z`;

  return d;
}

/**
 * WaveBG (BackGround) Component
 *
 * Renders an animated multi-layer sine wave background inside an SVG,
 * designed for use behind toolbars, headers, or as a decorative background.
 *
 * - Uses requestAnimationFrame for smooth animation.
 * - Each wave has randomized parameters (amplitude, speed, opacity, etc.).
 * - Performance optimized: viewport size is measured only on resize, not every frame.
 *
 * Props:
 * @param {number} waveCount - Number of individual wave paths to render.
 * @param {number} points    - Number of sample points per wave path.
 * @param {number} height    - Height of the SVG container.
 * @param {object} whiteBase - RGB object defining the base stroke color.
 * @param {string} className - Optional custom class for container div.
 * @param {object} style     - Optional inline styles for container div.
 */
export const WaveBG = ({
  waveCount = 6,
  points = 64,
  height = 80,
  whiteBase = { r: 255, g: 255, b: 255 },
  className = "",
  style = {},
  fullScreen = false,
}) => {
  const svgRef = useRef(null); // reference to the <svg> element
  const wavesRef = useRef([]); // array of refs for each <path>
  const animRef = useRef(null); // animation frame ID
  const configRef = useRef([]); // per-wave configuration (randomized)
  const sizeRef = useRef({ w: 1200, h: height }); // cached SVG size
  const startRef = useRef(performance.now());
  /**
   * Measure the SVG container size only on mount & resize.
   * This avoids calling getBoundingClientRect() every animation frame.
   */
  useEffect(() => {
    function updateSize() {
      if (fullScreen) {
        sizeRef.current = { w: window.innerWidth, h: window.innerHeight };
      } else {
        const rect = svgRef.current?.getBoundingClientRect();
        if (rect) sizeRef.current = { w: rect.width, h: rect.height };
      }
    }
    updateSize();
    const onResize = () => updateSize();
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [fullScreen]);

  /**
   * Initialize wave configurations and start animation loop.
   */
  useEffect(() => {
    // Randomize parameters for each wave layer
    configRef.current = new Array(waveCount).fill(0).map((cfg, i) => {
      const prevCfg = configRef.current[i] || {};
      return {
        ...prevCfg, // previus phase
        amplitude: prevCfg.amplitude ?? 6 + Math.random() * 30, // base amplitude
        frequency: prevCfg.frequency ?? 0.004 + Math.random() * 0.02, // horizontal density
        phase: prevCfg.phase ?? Math.random() * Math.PI * 2, // initial offset
        speed: prevCfg.speed ?? (i === 0 ? 0.08 : 0.3 + Math.random() * 0.2), // slower deep layer
        baseline: prevCfg.baseline ?? 0.2 + Math.random() * 0.6, // vertical offset
        opacity: prevCfg.opacity ?? 0.05 + Math.random() * 0.25, // stroke opacity
        strokeWidth: prevCfg.strokeWidth ?? 0.6 + Math.random() * 2.5, // stroke thickness
        ampOsc: prevCfg.ampOsc ?? 0.2 + Math.random() * 0.8, // oscillation of amplitude
        ampPhase: prevCfg.ampPhase ?? Math.random() * Math.PI * 2, // phase for amplitude oscillation
      };
    });

    function frame(now) {
      const t = (now - startRef.current) / 1750;
      const { w, h } = sizeRef.current;

      configRef.current.forEach((cfg, i) => {
        const el = wavesRef.current[i];
        if (!el) return;

        const amp =
          cfg.amplitude *
          (1 + Math.sin(t * 0.5 + cfg.ampPhase) * 0.08 * cfg.ampOsc);

        const phase = cfg.phase + t * cfg.speed * 2 * Math.PI;

        const d = createSinePath(
          w,
          h,
          points,
          amp,
          cfg.frequency,
          phase,
          cfg.baseline,
          12
        );

        el.setAttribute("d", d);
        el.setAttribute(
          "stroke",
          `rgba(${whiteBase.r}, ${whiteBase.g}, ${whiteBase.b}, ${cfg.opacity})`
        );
        el.setAttribute("stroke-width", `${cfg.strokeWidth}`);
      });

      animRef.current = requestAnimationFrame(frame);
    }

    animRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(animRef.current);
  }, [waveCount, points, whiteBase, fullScreen]);

  const containerStyle = fullScreen
    ? {
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        zIndex: -1,
        pointerEvents: "none",
        ...style,
      }
    : { height, ...style };

  return (
    <div
      className={`wave-bg ${className}`}
      aria-hidden="true"
      role="presentation"
      style={containerStyle}
    >
      <svg
        ref={svgRef}
        className="wave-svg"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
      >
        {new Array(waveCount).fill(0).map((_, i) => (
          <path
            key={i}
            ref={(el) => {
              if (el) wavesRef.current[i] = el;
            }}
            className="wave-path"
            d=""
            fill="transparent"
            strokeLinejoin="round"
            strokeLinecap="round"
            shapeRendering="geometricPrecision"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
    </div>
  );
};
